import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { MagnifyingGlass, PencilSimple, Trash, ArrowSquareOut, BookOpen } from "@phosphor-icons/react";
import { cookbookPath } from "@/lib/slug";
import CookbookEditDialog from "@/pages/admin/CookbookEditDialog";
import CookbookDeleteDialog from "@/pages/admin/CookbookDeleteDialog";

const PAGE = 50;

const CookbookRow = ({ c, onEdit, onDelete }) => (
  <tr className="border-t border-rf-ink/10" data-testid={`admin-cookbook-row-${c.cookbook_id}`}>
    <td className="p-3">
      <div className="flex items-center gap-3">
        {c.cover_image_url ? (
          <img src={c.cover_image_url} alt="" className="w-10 h-12 rounded-md object-cover shrink-0 border border-rf-ink/10" loading="lazy" />
        ) : (
          <div className="w-10 h-12 rounded-md bg-rf-cream-alt flex items-center justify-center shrink-0"><BookOpen size={16} className="text-rf-ink-soft" /></div>
        )}
        <div className="min-w-0">
          <p className="font-display font-extrabold text-rf-ink truncate" title={c.title}>{c.title || "Untitled"}</p>
          {c.description && <p className="text-[11px] text-rf-ink-soft truncate">{c.description}</p>}
        </div>
      </div>
    </td>
    <td className="p-3 text-xs text-rf-ink-soft"><p className="truncate" title={c.owner_email}>{c.owner_email || "—"}</p></td>
    <td className="p-3 text-xs text-rf-ink-soft" data-testid={`admin-cookbook-count-${c.cookbook_id}`}>{c.recipe_count}</td>
    <td className="p-3 text-[11px] font-bold uppercase tracking-wider">
      {c.privacy === "public" ? <span className="text-emerald-700">Public</span> : <span className="text-rf-ink-soft">Private</span>}
      {c.is_system && <span className="block text-[10px] text-rf-orange">System</span>}
    </td>
    <td className="p-3 text-right whitespace-nowrap">
      <button type="button" onClick={() => onEdit(c)} data-testid={`admin-cookbook-edit-${c.cookbook_id}`} className="inline-flex items-center gap-1 text-xs font-bold text-rf-orange hover:underline mr-3">
        <PencilSimple size={12} weight="bold" /> Edit
      </button>
      {!c.is_system && (
        <button type="button" onClick={() => onDelete(c)} data-testid={`admin-cookbook-delete-${c.cookbook_id}`} className="inline-flex items-center gap-1 text-xs font-bold text-red-600 hover:underline mr-3">
          <Trash size={12} weight="bold" /> Delete
        </button>
      )}
      <Link to={cookbookPath(c)} target="_blank" rel="noreferrer" data-testid={`admin-cookbook-view-${c.cookbook_id}`} className="inline-flex items-center gap-1 text-xs font-bold text-rf-ink-soft hover:text-rf-ink">
        <ArrowSquareOut size={12} weight="bold" /> View
      </Link>
    </td>
  </tr>
);

export default function CookbooksPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [privacy, setPrivacy] = useState("");
  const [showSystem, setShowSystem] = useState(false);
  const [skip, setSkip] = useState(0);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const reload = async (s = skip) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ q, privacy, include_system: String(showSystem), skip: String(s), limit: String(PAGE) });
      const { data } = await api.get(`/admin/cookbooks?${params.toString()}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load cookbooks");
    } finally { setLoading(false); }
  };

  useEffect(() => { setSkip(0); reload(0); /* eslint-disable-line */ }, [privacy, showSystem]);

  const page = (s) => { setSkip(s); reload(s); };

  return (
    <div data-testid="admin-cookbooks-panel">
      <p className="overline mb-2">Moderation</p>
      <h2 className="font-display text-3xl font-black text-rf-ink mb-1">All cookbooks</h2>
      <p className="text-rf-ink-soft mb-6 text-sm max-w-3xl">Every cookbook across every user. Edit the title, description, cover and visibility, or delete a cookbook with its recipes.</p>

      <div className="card-recipe !p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px] relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-rf-ink-soft" size={18} />
          <input data-testid="admin-cookbooks-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && page(0)} placeholder="Search by title or owner email…" className="input-rf !pl-10" />
        </div>
        <select data-testid="admin-cookbooks-privacy" value={privacy} onChange={(e) => setPrivacy(e.target.value)} className="input-rf max-w-[160px]">
          <option value="">All visibility</option>
          <option value="public">Public only</option>
          <option value="private">Private only</option>
        </select>
        <label className="flex items-center gap-2 text-xs font-bold text-rf-ink-soft">
          <input type="checkbox" data-testid="admin-cookbooks-show-system" checked={showSystem} onChange={(e) => setShowSystem(e.target.checked)} className="w-4 h-4" />
          Show Inboxes
        </label>
        <button data-testid="admin-cookbooks-search-btn" onClick={() => page(0)} className="btn-outline-ink !py-2 !px-4 text-sm">Search</button>
      </div>

      <div className="card-recipe !p-0 overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm table-fixed" data-testid="admin-cookbooks-table">
          <colgroup>
            <col style={{ width: "360px" }} /><col style={{ width: "220px" }} /><col style={{ width: "80px" }} /><col style={{ width: "100px" }} /><col style={{ width: "220px" }} />
          </colgroup>
          <thead className="bg-rf-cream-alt">
            <tr className="text-left text-xs font-black uppercase tracking-wider text-rf-ink-soft">
              <th className="p-3">Cookbook</th><th className="p-3">Owner</th><th className="p-3">Recipes</th><th className="p-3">Visibility</th><th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-6 text-center text-rf-ink-soft italic">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-rf-ink-soft italic">No cookbooks match.</td></tr>}
            {!loading && rows.map((c) => <CookbookRow key={c.cookbook_id} c={c} onEdit={setEditing} onDelete={setDeleting} />)}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4 text-sm text-rf-ink-soft">
        <span data-testid="admin-cookbooks-total">{total} cookbook{total === 1 ? "" : "s"}</span>
        <div className="flex gap-2">
          <button data-testid="admin-cookbooks-prev" disabled={skip === 0} onClick={() => page(Math.max(0, skip - PAGE))} className="btn-outline-ink !py-1 !px-3 text-xs disabled:opacity-40">Prev</button>
          <button data-testid="admin-cookbooks-next" disabled={skip + PAGE >= total} onClick={() => page(skip + PAGE)} className="btn-outline-ink !py-1 !px-3 text-xs disabled:opacity-40">Next</button>
        </div>
      </div>

      <CookbookEditDialog cookbook={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      <CookbookDeleteDialog cookbook={deleting} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); reload(); }} />
    </div>
  );
}
